import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, API, formatApiErrorDetail, friendlyError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Send, X as XIcon, Users, Instagram, Sparkles } from "lucide-react";
import { toast } from "sonner";

const WS_BASE = (() => {
  const url = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
  return url;
})();

export default function EventChatDialog({ event, open, onClose }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const scrollRef = useRef(null);
  const wsRef = useRef(null);

  // Load initial messages + attendees
  useEffect(() => {
    if (!open || !event) return;
    (async () => {
      try {
        const { data } = await api.get(`/events/${event.id}/messages`);
        setMessages(data);
      } catch (e) {
        toast.error(friendlyError(e));
      }
      try {
        const { data } = await api.get(`/events/${event.id}/attendees`);
        setAttendees(data);
      } catch (err) { /* ignore */ }
    })();
  }, [open, event]);

  // Setup WebSocket for realtime
  useEffect(() => {
    if (!open || !event) return;
    // Ask for notification permission once user opens any chat
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission();
      }
    } catch (err) { /* ignore */ }
    let cancelled = false;
    let ws;
    (async () => {
      try {
        const { data } = await api.get("/auth/ws-token");
        if (cancelled) return;
        ws = new WebSocket(`${WS_BASE}/api/ws/events/${event.id}?token=${encodeURIComponent(data.token)}`);
        wsRef.current = ws;
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            setMessages((prev) => prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]);
            // Notify if message is NOT mine and the tab isn't focused
            try {
              if (msg.user_id !== user?.id && !msg.is_system && typeof Notification !== "undefined" && Notification.permission === "granted" && document.hidden) {
                const n = new Notification(`${msg.user_name} · ${event.title}`, {
                  body: msg.text.slice(0, 140),
                  icon: event.photo_url || "/favicon.ico",
                  tag: `cy-${event.id}`,
                });
                n.onclick = () => { window.focus(); n.close(); };
              }
            } catch (err) { /* ignore */ }
          } catch (err) { /* ignore */ }
        };
        ws.onerror = () => {};
      } catch (err) { /* ignore */ }
    })();
    return () => {
      cancelled = true;
      try { ws?.close(); } catch (err) { /* ignore */ }
      wsRef.current = null;
    };
  }, [open, event, user?.id]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await api.post(`/events/${event.id}/messages`, { text: t });
      setText("");
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setSending(false);
    }
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="chat-dialog"
        className="max-w-md h-[85vh] sm:h-[600px] flex flex-col p-0 gap-0 rounded-2xl overflow-hidden bg-white"
      >
        <DialogHeader className="p-4 border-b border-slate-200 flex-row items-center gap-3 space-y-0">
          {event.photo_url && (
            <img src={event.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
          )}
          <div className="flex-1 min-w-0">
            <DialogTitle className="font-heading text-base font-extrabold text-slate-900 truncate">{event.title}</DialogTitle>
            <button
              data-testid="chat-toggle-members"
              onClick={() => setShowMembers((v) => !v)}
              className="text-xs text-sky-600 font-bold hover:text-sky-700 flex items-center gap-1"
            >
              <Users className="w-3 h-3" /> {attendees.length} {attendees.length === 1 ? "člen" : "členů"}
            </button>
          </div>
        </DialogHeader>

        {/* MEMBERS PANEL */}
        {showMembers && (
          <div data-testid="chat-members-panel" className="border-b border-slate-200 max-h-48 overflow-y-auto bg-slate-50/40">
            {attendees.map((a) => (
              <div key={a.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-white" data-testid={`chat-member-${a.id}`}>
                <div className="w-8 h-8 rounded-full bg-sky-100 text-sky-700 grid place-items-center font-extrabold text-xs">
                  {(a.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-900 truncate">{a.name}</div>
                  {a.bio && <div className="text-xs text-slate-500 truncate">{a.bio}</div>}
                </div>
                {a.instagram_handle && (
                  <a
                    href={`https://instagram.com/${a.instagram_handle}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs text-pink-500 hover:text-pink-600 font-bold flex items-center gap-1"
                    data-testid={`chat-member-ig-${a.id}`}
                  >
                    <Instagram className="w-3 h-3" />@{a.instagram_handle}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {/* MESSAGES */}
        <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2 bg-slate-50/30">
          {messages.length === 0 && (
            <div className="text-center text-slate-400 text-sm font-medium mt-8">
              Buď první, kdo něco napíše 👋
            </div>
          )}
          {messages.map((m) => {
            if (m.is_system) {
              return (
                <div key={m.id} className="flex justify-center my-2" data-testid={`chat-msg-system-${m.id}`}>
                  <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-slate-200 text-slate-600 font-bold">
                    <Sparkles className="w-3 h-3" /> {m.text}
                  </span>
                </div>
              );
            }
            const mine = m.user_id === user?.id;
            return (
              <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[80%]">
                  {!mine && (
                    <div className="text-xs font-bold text-slate-500 mb-0.5 ml-3">{m.user_name}</div>
                  )}
                  <div className={[
                    "px-3.5 py-2 rounded-2xl text-sm leading-snug",
                    mine ? "bg-sky-500 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm",
                  ].join(" ")}>
                    {m.text}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* INPUT */}
        <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2 items-center bg-white">
          <input
            data-testid="chat-input"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Napiš zprávu…"
            maxLength={1000}
            className="flex-1 px-4 py-2.5 rounded-full bg-slate-100 border border-transparent focus:bg-white focus:border-sky-300 focus:ring-4 focus:ring-sky-100 outline-none text-sm font-medium"
          />
          <button
            data-testid="chat-send-btn"
            type="submit" disabled={!text.trim() || sending}
            className="w-10 h-10 rounded-full bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white grid place-items-center active:scale-95 transition-all"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
